<script setup>
import { ref, computed, onMounted, watch } from "vue";
import ResultCard from "@components/ResultCard.vue";
import { useBookingStore } from "@stores/BookingStore";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  CustomMarker,
  CustomControl,
} from "vue3-google-map";
import Dialog from "primevue/dialog";
import axios from "axios";
import { formatPrice } from "@app/helpers";

const props = defineProps({
  opened: {
    type: Boolean,
    default: false,
  },
  clinics: {
    type: Array,
    required: false,
  },
  clinic: {
    type: Object,
    required: false,
  },
});

const bookingStore = useBookingStore();
const center = ref({ lat: -34.397, lng: 150.644 }); // Default center
const bounds = ref({});

const internalClinics = computed(() => {
  if (props.clinics && props.clinics.length > 0) {
    return props.clinics;
  }
  if (props.clinic && Object.keys(props.clinic).length > 0) {
    return [props.clinic]; // Use the single clinic as an array
  }
  return []; // Default to an empty array
});

// Extracting postcodes from the effective list of clinics
const postcodes = computed(() => {
  return internalClinics.value.map((clinic) => clinic.postcode);
});

console.log("Map Modal Loaded:", internalClinics.value);

const markerPositions = computed(() => {
  return internalClinics.value
    .map((clinic) => {
      const lat = Number(clinic.latitude);
      const lng = Number(clinic.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
      return null;
    })
    .filter(Boolean); // Remove any null entries if lat/lng were invalid
});

const mapRef = ref(null);
const selectedClinic = ref(null);

// Zoom functions for custom controls
const zoomIn = () => {
  if (mapRef.value?.map) {
    const currentZoom = mapRef.value.map.getZoom();
    mapRef.value.map.setZoom(currentZoom + 1);
  }
};

const zoomOut = () => {
  if (mapRef.value?.map) {
    const currentZoom = mapRef.value.map.getZoom();
    mapRef.value.map.setZoom(currentZoom - 1);
  }
};

initMap();

watch(
  () => mapRef.value?.ready,
  (ready) => {
    if (!ready) return;

    // give time for maps to load markers and info boxes before computing bounds.
    setTimeout(() => {
      bounds.value = new mapRef.value.api.LatLngBounds();
      markerPositions.value.forEach((marker) => {
        // markerPositions is now computed
        bounds.value.extend(marker);
      });

      if (mapRef.value.map.getZoom() > 15) {
        mapRef.value.map.setZoom(15);
      }
      // Fit bounds only if there are markers to fit
      if (markerPositions.value.length > 1) {
        mapRef.value.map.fitBounds(bounds.value);
        mapRef.value.map.setZoom(mapRef.value.map.getZoom() - 3);
      }

      mapRef.value.map.setCenter(center.value);
    }, 500);
  }
);

const showResultCard = () => {};

async function initMap() {
  if (internalClinics.value.length > 0) {
    const firstClinic = internalClinics.value[0];
    const lat = Number(firstClinic.latitude);
    const lng = Number(firstClinic.longitude);

    if (!isNaN(lat) && !isNaN(lng)) {
      center.value = { lat, lng };
    }
    // If lat/lng are invalid, center will retain its default value
  }
}
</script>

<template>
  <article class="relative h-full">
    <GoogleMap
      ref="mapRef"
      api-key="AIzaSyCEGZLjoa_3kTRwe8ZxxrvesQ6w4A-BnSc"
      style="width: 100%; height: 100%"
      :center="center"
      :zoom="15">
      <template #default="{ ready, api, map, mapTilesLoaded }">
        <!-- <CustomMarker class="z-20" :options="{ position: center }">
          <button
            @click="showResultCard"
            class="z-20 bg-blue px-40 py-10 border border-blue rounded-full font-medium text-20 text-white">
            £80
          </button>
        </CustomMarker> -->

        <CustomMarker
          class="z-10"
          v-for="(clinic, index) in internalClinics"
          :key="index"
          :options="{
            position: {
              lat: Number(clinic.latitude),
              lng: Number(clinic.longitude),
            },
          }">
          <button
            v-if="clinics"
            @click.stop="selectedClinic = index"
            class="px-40 py-10 border border-blue rounded-full font-medium text-20"
            :class="{
              'bg-white text-blue': selectedClinic !== index,
              'bg-blue text-white': selectedClinic === index,
            }">
            £{{ formatPrice(clinic.totalPrice) }}
          </button>
          <div
            v-else
            class="flex flex-center bg-blue rounded-full w-[40px] h-[40px] font-medium text-20 text-white">
            <i class="ri-first-aid-kit-line"></i>
          </div>
        </CustomMarker>

        <div class="bottom-10 absolute flex justify-center w-full">
          <div class="w-full max-w-[1000px]">
            <ResultCard
              v-if="selectedClinic !== null && clinics"
              :clinic="internalClinics[selectedClinic]" />
          </div>
        </div>

        <CustomControl position="BOTTOM_CENTER">
          <div class="flex gap-4 bg-white shadow-md mb-20 p-10 rounded-full">
            <button class="text-22 text-blue" @click="zoomIn">
              <i class="ri-zoom-in-line"></i>
            </button>
            <button class="text-22 text-blue" @click="zoomOut">
              <i class="ri-zoom-out-line"></i>
            </button>
          </div>
        </CustomControl>
      </template>
    </GoogleMap>
  </article>
</template>

<style scoped lang="scss"></style>
